import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Upload,
  List,
  Popconfirm,
  message as antMessage,
} from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useGetSuppliers } from "../../../hooks/useSuppliers";
import { useGetMaintenanceCompanies } from "../../../hooks/useCompanies";
import { useUploadPDF } from "../../../hooks/useEquipment";

const EquipmentFormModal = ({
  visible,
  onCancel,
  onSubmit,
  editingEquipment,
  isLoading,
}) => {
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState([]);
  const [docTitle, setDocTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: suppliers = [] } = useGetSuppliers();
  const { data: companies = [] } = useGetMaintenanceCompanies();
  const uploadPDFMutation = useUploadPDF();

  useEffect(() => {
    if (visible) {
      if (editingEquipment) {
        form.setFieldsValue({
          name: editingEquipment.name,
          code: editingEquipment.code,
          reciveDate: editingEquipment.reciveDate
            ? dayjs(editingEquipment.reciveDate)
            : null,
          supplierId: editingEquipment.supplierId,
          maintenanceCompanyId: editingEquipment.maintenanceCompanyId,
          mintanaceDate: editingEquipment.mintanaceDate
            ? dayjs(editingEquipment.mintanaceDate)
            : null,
          maintenanceReminderHours: editingEquipment.maintenanceReminderHours,
        });
        setDocuments(editingEquipment.equipmentDocs || []);
      } else {
        form.resetFields();
        setDocuments([]);
      }
      setDocTitle("");
    }
  }, [visible, editingEquipment, form]);

  const handleUploadDoc = async (file) => {
    if (!docTitle.trim()) {
      antMessage.error("Please enter a document title");
      return false;
    }

    setUploading(true);
    try {
      const uploadResult = await uploadPDFMutation.mutateAsync(file);

      const newDoc = {
        title: docTitle,
        docId: uploadResult.id,
      };

      setDocuments((prev) => [...prev, newDoc]);
      setDocTitle("");
      antMessage.success("Document uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }

    return false;
  };

  const handleDeleteDoc = (docToDelete) => {
    setDocuments((prev) =>
      prev.filter((doc) => doc.docId !== docToDelete.docId),
    );
    antMessage.success("Document removed");
  };
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Format dates to ISO string and include documents
      const formattedValues = {
        ...values,
        reciveDate: values.reciveDate ? values.reciveDate.toISOString() : null,
        mintanaceDate: values.mintanaceDate
          ? values.mintanaceDate.toISOString()
          : null,
        equipmentDocs: documents.map((doc) => ({
          title: doc.title,
          docId: doc.docId,
        })),
      };

      await onSubmit(formattedValues);
      if (!isLoading) {
        form.resetFields();
        setDocuments([]);
        setDocTitle("");
      }
    } catch (err) {
      console.error("Submission error:", err);
    }
  };

  return (
    <Modal
      title={editingEquipment ? "Edit Equipment" : "Add Equipment"}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSubmit}
          loading={isLoading}
          style={{ backgroundColor: "#298d9f", borderColor: "#298d9f" }}
        >
          Save
        </Button>,
      ]}
      destroyOnClose
      width={900}
    >
      <Form form={form} layout="vertical">
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="name"
            label="Equipment Name"
            rules={[
              { required: true, message: "Please enter equipment name" },
              { max: 100, message: "Name cannot exceed 100 characters" },
            ]}
          >
            <Input placeholder="Enter equipment name" />
          </Form.Item>

          <Form.Item
            name="code"
            label="Equipment Code"
            rules={[
              { required: true, message: "Please enter equipment code" },
              { max: 50, message: "Code cannot exceed 50 characters" },
            ]}
          >
            <Input placeholder="Enter equipment code" />
          </Form.Item>

          <Form.Item
            name="reciveDate"
            label="Receive Date"
            rules={[{ required: true, message: "Please select receive date" }]}
          >
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="supplierId"
            label="Supplier"
            rules={[{ required: true, message: "Please select a supplier" }]}
          >
            <Select
              placeholder="Select supplier"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {suppliers.map((supplier) => (
                <Select.Option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="maintenanceCompanyId"
            label="Maintenance Company"
            rules={[
              {
                required: true,
                message: "Please select a maintenance company",
              },
            ]}
          >
            <Select
              placeholder="Select maintenance company"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {companies.map((company) => (
                <Select.Option key={company.id} value={company.id}>
                  {company.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="mintanaceDate"
            label="Maintenance Date"
            rules={[
              { required: true, message: "Please select maintenance date" },
            ]}
          >
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="maintenanceReminderHours"
            label="Maintenance Reminder (Hours)"
            rules={[
              { required: true, message: "Please enter reminder hours" },
              { type: "number", min: 0, message: "Hours must be positive" },
            ]}
          >
            <InputNumber className="w-full" placeholder="Enter hours" min={0} />
          </Form.Item>
        </div>

        {/* Documents Section */}
        <div className="border-t pt-4 mt-4">
          <h3 className="text-base font-semibold mb-3 text-gray-700">
            Equipment Documents <span className="font-normal text-sm"> (You Must Add Title firstly To Upload) </span>
          </h3>

          <div className="flex gap-2 mb-3">
            <Input
              placeholder="Enter document title"
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              style={{ flex: 1 }}
            />
            <Upload
              beforeUpload={handleUploadDoc}
              showUploadList={false}
              accept=".pdf"
              
              disabled={!docTitle.trim() || uploading}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                disabled={!docTitle.trim()}
                >
                Upload PDF
              </Button>
            </Upload>
                  
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Only PDF files are allowed
          </p>

          <List
            size="small"
            bordered
            dataSource={documents}
            locale={{ emptyText: "No documents uploaded yet" }}
            renderItem={(doc) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="delete"
                    title="Are you sure to remove this document?"
                    onConfirm={() => handleDeleteDoc(doc)} // Pass entire doc object
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      size="small"
                    >
                      Remove
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <div>
                  <span className="font-medium">{doc.title}</span>
                  {doc.isNew && (
                    <span className="ml-2 text-xs text-green-600">(New)</span>
                  )}
                </div>
              </List.Item>
            )}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default EquipmentFormModal;
